import { ComponentType, SVGProps } from 'react'
import { Box, HStack, Text, VStack } from '@chakra-ui/react'
import AccountTypeAthleteIcon from 'assets/icons/account-type-athlete.svg?react'
import AccountTypeCoachIcon from 'assets/icons/account-type-coach.svg?react'
import AccountTypeGuardianIcon from 'assets/icons/account-type-guardian.svg?react'
import { AddableProfileType, PROFILE_TYPE_LABEL } from 'utils/profile-eligibility'

export interface ProfileTypeOption {
  type: AddableProfileType
  description: string
  /** When true, the card renders at reduced opacity with a not-allowed cursor. */
  disabled?: boolean
  /** Reason string shown in place of description when disabled. */
  disabledReason?: string
}

interface Props {
  /** Currently selected type; null = nothing selected. */
  value: AddableProfileType | null
  onChange: (t: AddableProfileType) => void
  options: ProfileTypeOption[]
  disabled?: boolean
}

const TYPE_ICON: Record<AddableProfileType, ComponentType<SVGProps<SVGSVGElement>>> = {
  player: AccountTypeAthleteIcon,
  coach: AccountTypeCoachIcon,
  youth: AccountTypeGuardianIcon
}

/**
 * Shared profile-type card picker.
 * Used by AddProfileModal (pick step) and InviteModal.
 * Renders every option in `options` with selection highlight.
 */
const ProfileTypePicker = ({ value, onChange, options, disabled = false }: Props) => (
  <VStack spacing="10px" align="stretch" pb={2}>
    {options.map(({ type, description, disabled: optDisabled, disabledReason }) => {
      const Icon = TYPE_ICON[type]
      const isDisabled = disabled || optDisabled
      const desc = isDisabled && disabledReason ? disabledReason : description
      const isSelected = value === type

      return (
        <Box
          key={type}
          as="button"
          type="button"
          onClick={!isDisabled ? () => onChange(type) : undefined}
          disabled={isDisabled}
          p="14px"
          borderRadius="12px"
          bg={isSelected ? 'rgba(46,143,204,0.12)' : 'transparent'}
          border="1px solid"
          borderColor={isSelected ? 'rgba(46,143,204,0.60)' : 'rgba(194, 199, 214, 0.30)'}
          transition="all 0.15s ease"
          textAlign="left"
          opacity={isDisabled ? 0.45 : 1}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          _hover={
            !isDisabled
              ? { borderColor: 'rgba(194, 199, 214, 0.55)', bg: isSelected ? 'rgba(46,143,204,0.15)' : 'rgba(255,255,255,0.03)' }
              : undefined
          }
          _focusVisible={
            !isDisabled
              ? { outline: '2px solid', outlineColor: 'blue.400', outlineOffset: '2px' }
              : undefined
          }
          aria-pressed={isSelected}
          aria-disabled={isDisabled}
        >
          <HStack spacing="12px" align="center">
            <Box
              color="rgba(244, 248, 251, 0.85)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon width={28} height={28} />
            </Box>
            <VStack spacing="2px" align="flex-start" flex={1} minW={0}>
              <Text fontSize="15px" fontWeight={600} color="white" m={0} lineHeight="1.3">
                {PROFILE_TYPE_LABEL[type]}
              </Text>
              <Text fontSize="13px" color="rgba(244, 248, 251, 0.62)" m={0} lineHeight="1.3">
                {desc}
              </Text>
            </VStack>
          </HStack>
        </Box>
      )
    })}
  </VStack>
)

export default ProfileTypePicker
